import { reactive } from 'vue';

/**
 * Reactive cache for the Tax Question pages (S1-P2). The live API
 * (`services/taxQuestionApi.js`) is the source of truth; this store holds the
 * mapped, UI-shaped copy that the views/dialogs render and bridges the API DTO
 * (camelCase) ⇄ the request payload, applying the naming canon.
 *
 * 🆕 Opening-question augment: each item is a conversational "opening question"
 * bound to a single tax rule, carrying `questionTextTh`/`systemPrompt`/
 * `answerType`/`controlType`/`answerOptions`/`pndCodes`. Flow steps (conversation
 * phases) carry step-level `systemPrompt` + `knowledgeIds` and are edited inline
 * in the builder (keyed by `groupCode === stepCode`).
 *
 * Naming canon (canonical across S1-P2 ⇄ S1-P3):
 *  - `ruleCode`, `groupCode`, `stepCode`, `itemKind` → trim + UPPER_SNAKE_CASE.
 *  - `fieldKey` → trim ONLY (NEVER uppercased — it is consumed verbatim as a
 *    formula variable name by S1-P3's engine).
 *  - `inputType` / `answerType` / `controlType` are fixed lower-case enum tokens.
 *
 * @typedef {Object} TaxQuestionVersionDto
 * @property {number} questionVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {string} description @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionItemDto
 * @property {number} questionItemID @property {number} questionVersionID @property {string} itemKind
 * @property {?string} ruleCode @property {?string} fieldKey @property {string} label @property {?string} helpText
 * @property {?string} questionTextTh @property {?string} systemPrompt
 * @property {?string} answerGuidanceTh @property {boolean} showRuleCap
 * @property {string} inputType @property {?string} answerType @property {?string} controlType
 * @property {?Object[]} options @property {?string[]} answerOptions @property {?string[]} pndCodes
 * @property {string} visibleWhen @property {boolean} isRequired @property {?string} groupCode
 * @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} UiTaxQuestionItem A render/edit-friendly copy of TaxQuestionItemDto
 *   carrying a stable local key (`_uid`) for v-for / dialog identity.
 *
 * @typedef {Object} TaxQuestionStepDto
 * @property {number} questionStepID @property {string} stepCode @property {string} label @property {?string} description
 * @property {?string} systemPrompt @property {?string[]} knowledgeIds @property {boolean} perPerson @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxQuestionGroupDto
 * @property {string} groupCode @property {string} groupLabel @property {?string} systemPrompt @property {?string[]} knowledgeIds
 * @property {boolean} perPerson @property {number} sortOrder @property {TaxQuestionItemDto[]} items
 *
 * @typedef {Object} TaxQuestionFullDto
 * @property {TaxQuestionVersionDto} version @property {TaxQuestionStepDto[]} steps
 * @property {TaxQuestionGroupDto[]} groups @property {string[]} warnings
 */

// Known item kinds + input types (mirror backend enum tokens).
export const ITEM_KINDS = ['RULE_INPUT', 'PERSONAL_INFO', 'SPECIAL_INPUT', 'GROUP_INPUT', 'INCOME_CLASSIFIER', 'CONFIRM', 'LAYOUT'];
export const INPUT_TYPES = ['textbox', 'number', 'radio', 'checkbox', 'select', 'date', 'section_header', 'info_banner'];
export const LAYOUT_INPUT_TYPES = ['section_header', 'info_banner'];
export const OPTION_INPUT_TYPES = ['radio', 'checkbox', 'select'];

// 🆕 Opening-question answer kinds (high-level) → render widget (controlType).
export const ANSWER_TYPES = ['choice', 'yes_no', 'checkbox_group', 'amount', 'number', 'text', 'summary'];
export const ANSWER_TYPE_OPTIONS = [
  { value: 'choice', label: 'ตัวเลือก (เลือก 1 ค่า)' },
  { value: 'yes_no', label: 'ใช่ / ไม่ใช่' },
  { value: 'checkbox_group', label: 'เลือกได้หลายค่า' },
  { value: 'amount', label: 'จำนวนเงิน (บาท)' },
  { value: 'number', label: 'ตัวเลข / จำนวน' },
  { value: 'text', label: 'ข้อความอิสระ' },
  { value: 'summary', label: 'สรุป (คำนวณให้อัตโนมัติ)' },
];
export const ANSWER_TYPE_LABELS = ANSWER_TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
/** Answer kinds where tappable suggested answers are especially worth adding. */
export const OPTION_ANSWER_TYPES = ['choice', 'yes_no', 'checkbox_group'];

// 🆕 PND form tags (subset) + Thai legend (mirror the mock).
export const PND_CODES = ['PND90', 'PND91', 'PND94'];
export const PND_LEGEND = {
  PND90: 'ภ.ง.ด.90 — ยื่นเต็มปี (มีเงินได้หลายประเภท)',
  PND91: 'ภ.ง.ด.91 — ยื่นเต็มปี (เงินเดือนอย่างเดียว)',
  PND94: 'ภ.ง.ด.94 — ยื่นกลางปี',
};

// 🆕 Knowledge catalog (soft refs) — mirrors flowConfigStore.getKnowledgeCatalog().
export const KNOWLEDGE_CATALOG = [
  { knowledgeCode: 'KB_TAX_RULES', title: 'คู่มือกฎภาษีและค่าลดหย่อน' },
  { knowledgeCode: 'KB_PERSONAL_PROFILE', title: 'แนวทางสอบถามข้อมูลส่วนตัว' },
  { knowledgeCode: 'KB_INCOME_M40', title: 'คู่มือรายได้ ม.40(1)–(8)' },
  { knowledgeCode: 'KB_DEDUCTION_GUIDE', title: 'คู่มือค่าลดหย่อนและสิทธิประโยชน์' },
  { knowledgeCode: 'KB_RD_MANUAL', title: 'เอกสารกรมสรรพากร (rd.go.th)' },
  { knowledgeCode: 'KB_PRODUCT_CATALOG', title: 'แคตตาล็อกผลิตภัณฑ์ลดหย่อนภาษี' },
];

// Free-text visible-when grammar hints (mirror the mock's datalist).
export const VISIBLE_WHEN_HINTS = [
  'Always',
  'marital_status = married',
  'sex = female',
  'form_type in 90, 94',
];

// Canonical personal_info / special_input field keys (S1-P3 variable names).
export const PERSONAL_INFO_FIELD_KEYS = ['sex', 'age', 'marital_status', 'spouse_info_status', 'date_of_birth', 'nationality', 'tax_residency'];
export const SPECIAL_INPUT_FIELD_KEYS = ['withholding_tax', 'form_type', 'tax_year'];
// Default section grouping codes.
export const GROUP_CODES = ['PERSONAL', 'INCOME', 'DEDUCTION', 'SUMMARY'];

const GROUP_LABELS = {
  PERSONAL: 'ข้อมูลส่วนตัว',
  INCOME: 'รายได้',
  DEDUCTION: 'ค่าลดหย่อน',
  SUMMARY: 'สรุป',
  WITHHOLDING: 'ภาษีหัก ณ ที่จ่าย',
  GENERAL: 'ทั่วไป',
};

let uidSeq = 1;
function nextUid() {
  uidSeq += 1;
  return uidSeq;
}

const state = reactive({
  /** @type {?number} currently selected Tax Config Version (FK) */
  configVersionId: null,
  /** @type {TaxQuestionVersionDto[]} question versions of the selected config version */
  versions: [],
  /** @type {?TaxQuestionVersionDto} version currently open in the builder */
  currentVersion: null,
  /** @type {UiTaxQuestionItem[]} flat, ordered working copy of items in the builder */
  items: [],
  /**
   * Per-group/step metadata (system prompts + knowledge), keyed by UPPER
   * groupCode (== stepCode). The single source the builder edits inline and
   * serializes to the bulk-save `steps[]` payload.
   * @type {Object<string,{questionStepID:?number,stepCode:string,label:string,description:?string,systemPrompt:string,answerSystemPrompt:string,questionSystemPrompt:string,knowledgeIds:string[],sortOrder:number,isActive:boolean}>}
   */
  groupMeta: {},
  /** @type {string[]} non-blocking orphan-RuleCode warnings from getFull / bulk-save */
  warnings: [],
});

function trimUpper(value) {
  return String(value ?? '').trim().toUpperCase();
}
function trimOnly(value) {
  return String(value ?? '').trim();
}

export function groupLabel(groupCode) {
  const code = trimUpper(groupCode);
  return GROUP_LABELS[code] || code || 'ทั่วไป';
}

/**
 * Derive the render widget (controlType) from the high-level answer kind.
 * Mirrors the mock's `controlTypeForAnswer` + the BAL derivation.
 * @param {?string} answerType
 * @returns {?string}
 */
function controlTypeForAnswer(answerType) {
  switch (answerType) {
    case 'amount': return 'amount_input';
    case 'number': return 'number_input';
    case 'checkbox_group': return 'checkbox_group';
    case 'choice':
    case 'yes_no': return 'option_cards';
    case 'summary': return 'summary_card';
    case 'text': return 'text_input';
    default: return null;
  }
}

/**
 * Map a high-level answer kind → a legacy `inputType` enum token, so the
 * structured options round-trip + LAYOUT detection keep working.
 * @param {?string} answerType
 * @returns {string}
 */
function inputTypeForAnswer(answerType) {
  switch (answerType) {
    case 'amount':
    case 'number': return 'number';
    case 'choice':
    case 'yes_no': return 'radio';
    case 'checkbox_group': return 'checkbox';
    default: return 'textbox';
  }
}

/** Trim + dedupe a string array (optionally transforming each value first). */
function uniqueStrings(values, mapFn) {
  if (!Array.isArray(values)) return [];
  const out = [];
  const seen = new Set();
  values.forEach((v) => {
    const s = String((mapFn ? mapFn(v) : v) ?? '').trim();
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

function setConfigVersion(id) {
  state.configVersionId = id != null ? Number(id) : null;
}

function setVersions(dtoArray) {
  state.versions = Array.isArray(dtoArray) ? dtoArray.slice() : [];
}

/**
 * Map a TaxQuestionItemDto (camelCase) → UI item (camelCase + `_uid`).
 * @param {TaxQuestionItemDto} dto
 * @returns {UiTaxQuestionItem}
 */
function mapItemDtoToUi(dto) {
  return {
    _uid: nextUid(),
    questionItemID: dto.questionItemID ?? null,
    questionVersionID: dto.questionVersionID ?? null,
    itemKind: dto.itemKind || 'RULE_INPUT',
    ruleCode: dto.ruleCode ?? null,
    fieldKey: dto.fieldKey ?? null,
    // 🆕 opening-question fields
    questionTextTh: dto.questionTextTh ?? '',
    systemPrompt: dto.systemPrompt ?? '',
    // 🆕 split per-question prompts (Answer = extracting, Question = asking)
    answerSystemPrompt: dto.answerSystemPrompt ?? '',
    questionSystemPrompt: dto.questionSystemPrompt ?? '',
    // 🆕(rev2) answer-guidance bubble + per-item rule cap flag
    answerGuidanceTh: dto.answerGuidanceTh ?? '',
    showRuleCap: Boolean(dto.showRuleCap),
    // 🆕 per-question quick-reply generation (toggle + prompt) — replaces the manual answerOptions authoring.
    quickReplyEnabled: Boolean(dto.quickReplyEnabled),
    quickReplyPrompt: dto.quickReplyPrompt ?? '',
    answerType: dto.answerType ?? null,
    controlType: dto.controlType ?? null,
    answerOptions: Array.isArray(dto.answerOptions) ? dto.answerOptions.slice() : [],
    pndCodes: Array.isArray(dto.pndCodes) ? dto.pndCodes.slice() : [],
    // 🆕 group question — bound fields (fieldKey kept verbatim, not uppercased).
    boundFields: Array.isArray(dto.boundFields)
      ? dto.boundFields.map((bf) => ({
        fieldKind: bf.fieldKind,
        ruleCode: bf.ruleCode || null,
        fieldKey: bf.fieldKey || null,
        label: bf.label || '',
        isRequired: !!bf.isRequired,
      }))
      : [],
    // 🆕 income classifier — category catalog (flagKey kept verbatim, not uppercased).
    incomeCategories: Array.isArray(dto.incomeCategories)
      ? dto.incomeCategories.map((c) => ({
        code: c.code || '',
        labelTh: c.labelTh || '',
        flagKey: c.flagKey || '',
        keywords: Array.isArray(c.keywords) ? c.keywords.slice() : [],
      }))
      : [],
    // 🆕 per-question knowledge refs (vectordbnames) — mirrors section knowledgeIds.
    knowledgeIds: Array.isArray(dto.knowledgeIds) ? dto.knowledgeIds.slice() : [],
    // existing fields
    label: dto.label || '',
    helpText: dto.helpText ?? null,
    inputType: dto.inputType || 'textbox',
    options: Array.isArray(dto.options) ? dto.options.map((o) => ({ ...o })) : null,
    visibleWhen: dto.visibleWhen || 'Always',
    isRequired: Boolean(dto.isRequired),
    groupCode: dto.groupCode ?? null,
    sortOrder: dto.sortOrder ?? 0,
    isActive: dto.isActive !== false,
  };
}

/**
 * Map a UI item → CreateTaxQuestionItemRequest (camelCase) for create / bulk.
 * Applies the naming canon (ruleCode/groupCode UPPER, fieldKey trim-only) and
 * the opening-question fields (answerType/controlType/answerOptions/pndCodes).
 * @param {UiTaxQuestionItem} item
 * @param {number} [sortOrder] override sort order (e.g. position in the builder)
 * @returns {Object}
 */
function buildItemRequest(item, sortOrder) {
  const kind = trimUpper(item.itemKind);
  const isRuleInput = kind === 'RULE_INPUT';
  const isLayout = kind === 'LAYOUT';
  const isGroupInput = kind === 'GROUP_INPUT';
  const isIncomeClassifier = kind === 'INCOME_CLASSIFIER';
  // 🆕 CONFIRM — a summary/confirmation checkpoint. Binds NO tax rule / personal
  // field, but carries a stable confirm key in fieldKey (trim-only, verbatim) plus
  // authored questionTextTh + question/answer system prompts.
  const isConfirm = kind === 'CONFIRM';
  const usesFieldKey = kind === 'PERSONAL_INFO' || kind === 'SPECIAL_INPUT' || isConfirm;
  const opts = OPTION_INPUT_TYPES.includes(item.inputType)
    && Array.isArray(item.options)
    && item.options.length
    ? item.options
      .map((o) => ({
        value: trimOnly(o.value),
        label: trimOnly(o.label),
        ...(o.visibleWhen ? { visibleWhen: o.visibleWhen } : {}),
      }))
      .filter((o) => o.value !== '' || o.label !== '')
    : null;
  // 🆕 answerType / controlType (controlType: keep explicit chat_prompt override, else derive).
  // GROUP_INPUT is conversational (user types freely, LLM splits the answer): no
  // answerType, and controlType defaults to text_input unless overridden to chat_prompt.
  const answerType = (isLayout || isGroupInput || isConfirm) ? null : (item.answerType ? trimOnly(item.answerType) : null);
  let controlType = null;
  if (isGroupInput) {
    controlType = item.controlType === 'chat_prompt' ? 'chat_prompt' : 'text_input';
  } else if (!isLayout && !isConfirm) {
    if (item.controlType === 'chat_prompt') controlType = 'chat_prompt';
    else controlType = answerType ? controlTypeForAnswer(answerType) : (item.controlType || null);
  }
  const answerOptions = uniqueStrings(item.answerOptions, (v) => String(v ?? ''));
  const pndCodes = uniqueStrings(item.pndCodes, (v) => trimUpper(v));
  // 🆕 group question — bound fields: only for GROUP_INPUT, else null (gated like
  // ruleCode/fieldKey). ruleCode → trim+UPPER; fieldKey → trim ONLY (verbatim).
  const boundFields = isGroupInput
    ? (Array.isArray(item.boundFields) ? item.boundFields : [])
      .map((bf) => {
        const fieldKind = trimUpper(bf.fieldKind);
        const isRuleBound = fieldKind === 'RULE_INPUT';
        return {
          fieldKind,
          ruleCode: isRuleBound ? (trimUpper(bf.ruleCode) || null) : null,
          fieldKey: isRuleBound ? null : (trimOnly(bf.fieldKey) || null),
          label: trimOnly(bf.label),
          isRequired: Boolean(bf.isRequired),
        };
      })
      .filter((bf) => bf.ruleCode || bf.fieldKey)
    : null;
  // 🆕 income classifier — category catalog: only for INCOME_CLASSIFIER, else null.
  // flagKey → trim ONLY (verbatim, consumed as a formula variable name); code → trim
  // ONLY (verbatim); drop entries missing code/flagKey/labelTh.
  const incomeCategories = isIncomeClassifier
    ? (Array.isArray(item.incomeCategories) ? item.incomeCategories : [])
      .map((c) => ({
        code: trimOnly(c.code),
        labelTh: trimOnly(c.labelTh),
        flagKey: trimOnly(c.flagKey),
        keywords: uniqueStrings(c.keywords, (v) => String(v ?? '')),
      }))
      .filter((c) => c.code && c.flagKey && c.labelTh)
    : null;
  return {
    itemKind: kind,
    ruleCode: isRuleInput ? trimUpper(item.ruleCode) || null : null,
    fieldKey: usesFieldKey ? trimOnly(item.fieldKey) || null : null,
    // 🆕 opening-question fields
    questionTextTh: item.questionTextTh ? trimOnly(item.questionTextTh) : null,
    systemPrompt: item.systemPrompt ? trimOnly(item.systemPrompt) : null,
    // 🆕 split per-question prompts (Answer = extracting, Question = asking)
    answerSystemPrompt: item.answerSystemPrompt ? trimOnly(item.answerSystemPrompt) : null,
    questionSystemPrompt: item.questionSystemPrompt ? trimOnly(item.questionSystemPrompt) : null,
    // 🆕(rev2) answer-guidance bubble + per-item rule cap flag
    answerGuidanceTh: item.answerGuidanceTh ? trimOnly(item.answerGuidanceTh) : null,
    showRuleCap: Boolean(item.showRuleCap),
    // 🆕 per-question quick-reply generation (toggle + prompt)
    quickReplyEnabled: Boolean(item.quickReplyEnabled),
    quickReplyPrompt: item.quickReplyPrompt ? trimOnly(item.quickReplyPrompt) : null,
    answerType,
    controlType,
    answerOptions,
    pndCodes,
    // 🆕 group question — bound fields (null unless GROUP_INPUT)
    boundFields,
    // 🆕 income classifier — category catalog (null unless INCOME_CLASSIFIER)
    incomeCategories,
    // 🆕 per-question knowledge refs (vectordbnames) — mirrors buildSteps() knowledgeIds
    knowledgeIds: uniqueStrings(item.knowledgeIds, (v) => String(v ?? '')),
    // existing fields
    label: trimOnly(item.label),
    helpText: item.helpText ? trimOnly(item.helpText) : null,
    inputType: item.inputType || 'textbox',
    options: opts,
    visibleWhen: trimOnly(item.visibleWhen) || 'Always',
    // 🆕 GROUP_INPUT no longer has a question-level "required" switch — the item is
    // required iff at least one of its (resolved) bound fields is required. Other
    // kinds (LAYOUT, legacy data) keep the item-level flag as-is.
    isRequired: isGroupInput
      ? (boundFields || []).some((f) => f.isRequired)
      : Boolean(item.isRequired),
    groupCode: item.groupCode ? trimUpper(item.groupCode) : null,
    sortOrder: sortOrder != null ? Number(sortOrder) : (Number(item.sortOrder) || 0),
    isActive: item.isActive !== false,
  };
}

/**
 * Build the bulk-save `steps[]` payload from `groupMeta`, ordered by the given
 * group codes (builder display order). Skips the synthetic `GENERAL` bucket.
 * @param {string[]} [groupCodesInOrder]
 * @returns {Object[]}
 */
function buildSteps(groupCodesInOrder) {
  const codes = Array.isArray(groupCodesInOrder) && groupCodesInOrder.length
    ? groupCodesInOrder
    : Object.keys(state.groupMeta);
  const seen = new Set();
  const steps = [];
  codes.forEach((raw) => {
    const code = trimUpper(raw);
    if (!code || code === 'GENERAL' || seen.has(code)) return;
    seen.add(code);
    const meta = state.groupMeta[code] || {};
    steps.push({
      stepCode: code,
      label: trimOnly(meta.label) || code,
      description: meta.description ? trimOnly(meta.description) : null,
      systemPrompt: meta.systemPrompt ? trimOnly(meta.systemPrompt) : null,
      // 🆕 split section prompts (Answer = extracting, Question = asking)
      answerSystemPrompt: meta.answerSystemPrompt ? trimOnly(meta.answerSystemPrompt) : null,
      questionSystemPrompt: meta.questionSystemPrompt ? trimOnly(meta.questionSystemPrompt) : null,
      knowledgeIds: uniqueStrings(meta.knowledgeIds, (v) => String(v ?? '')),
      // 🆕 ยื่นร่วม (joint filing): repeat this section per person when the form is joint.
      perPerson: Boolean(meta.perPerson),
      sortOrder: steps.length + 1,
      isActive: meta.isActive !== false,
    });
  });
  return steps;
}

/** Get-or-create the metadata bucket for a group/step (reactive). */
function ensureGroupMeta(groupCode, label) {
  const code = trimUpper(groupCode);
  if (!code) return null;
  if (!state.groupMeta[code]) {
    state.groupMeta[code] = {
      questionStepID: null,
      stepCode: code,
      label: label || groupLabel(code),
      description: null,
      systemPrompt: '',
      answerSystemPrompt: '',
      questionSystemPrompt: '',
      knowledgeIds: [],
      perPerson: false,
      sortOrder: 0,
      isActive: true,
    };
  }
  return state.groupMeta[code];
}

/** Ensure metadata buckets exist for every supplied group code. */
function ensureGroupMetaForCodes(codes) {
  (codes || []).forEach((code) => ensureGroupMeta(code));
}

/** Pure lookup of a group/step metadata bucket (no mutation). */
function groupMetaFor(groupCode) {
  return state.groupMeta[trimUpper(groupCode)] || null;
}

/** Set the step-level system prompt for a group (creates the bucket if needed). */
function setGroupSystemPrompt(groupCode, value) {
  const meta = ensureGroupMeta(groupCode);
  if (meta) meta.systemPrompt = value ?? '';
}

/** Set the step-level knowledge ids for a group (creates the bucket if needed). */
function setGroupKnowledge(groupCode, ids) {
  const meta = ensureGroupMeta(groupCode);
  if (meta) meta.knowledgeIds = Array.isArray(ids) ? ids.slice() : [];
}

/** Patch one or more step-level metadata fields for a group (creates if needed). */
function updateGroupMeta(groupCode, patch) {
  const meta = ensureGroupMeta(groupCode);
  if (meta && patch && typeof patch === 'object') Object.assign(meta, patch);
  return meta;
}

/**
 * Generate a unique opaque StepCode for a new free-form Section (`SEC_` + short
 * suffix, UPPER). The Section's Thai name lives in `Label` — the code is NOT a
 * slug of the name. Uniqueness is enforced against current `groupMeta` keys AND
 * any item `groupCode`s in the working set.
 * @returns {string}
 */
function generateStepCode() {
  const used = new Set(Object.keys(state.groupMeta));
  state.items.forEach((it) => {
    const c = trimUpper(it.groupCode);
    if (c) used.add(c);
  });
  let code;
  do {
    const suffix = Math.random().toString(36).slice(2, 8).toUpperCase();
    code = `SEC_${suffix}`;
  } while (!code.replace('SEC_', '') || used.has(code));
  return code;
}

/**
 * Create a new free-form Section: auto-generate a unique StepCode, seed its meta
 * with the given (Thai) name, and append it after the existing sections.
 * @param {string} label section name (Thai) → stored as `Label`
 * @returns {string} the generated StepCode
 */
function createSection(label) {
  const code = generateStepCode();
  const maxSort = Object.values(state.groupMeta)
    .reduce((m, x) => Math.max(m, Number(x.sortOrder) || 0), 0);
  state.groupMeta[code] = {
    questionStepID: null,
    stepCode: code,
    label: trimOnly(label) || code,
    description: null,
    systemPrompt: '',
    answerSystemPrompt: '',
    questionSystemPrompt: '',
    knowledgeIds: [],
    perPerson: false,
    sortOrder: maxSort + 1,
    isActive: true,
  };
  return code;
}

/** Remove a Section (its meta) and every item bound to it (by groupCode). */
function removeSection(groupCode) {
  const code = trimUpper(groupCode);
  if (!code) return;
  delete state.groupMeta[code];
  state.items = state.items.filter((it) => trimUpper(it.groupCode) !== code);
}

/**
 * Create a blank UI item seeded with a kind + group, ready for the dialog.
 * @param {Object} [seed]
 * @returns {UiTaxQuestionItem}
 */
function createBlankItem(seed = {}) {
  const kind = seed.itemKind || 'PERSONAL_INFO';
  const isLayout = kind === 'LAYOUT';
  // 🆕 CONFIRM binds no field and renders no widget — no answerType/controlType,
  // just a confirm key (fieldKey) + authored questionText + system prompts.
  const isConfirm = kind === 'CONFIRM';
  const answerType = (isLayout || isConfirm) ? null : (seed.answerType || 'text');
  return {
    _uid: nextUid(),
    questionItemID: null,
    questionVersionID: null,
    itemKind: kind,
    ruleCode: seed.ruleCode ?? null,
    fieldKey: seed.fieldKey ?? null,
    // 🆕 opening-question fields
    questionTextTh: seed.questionTextTh ?? '',
    systemPrompt: '',
    // 🆕 split per-question prompts (Answer = extracting, Question = asking)
    answerSystemPrompt: '',
    questionSystemPrompt: '',
    // 🆕(rev2) answer-guidance bubble + per-item rule cap flag
    answerGuidanceTh: seed.answerGuidanceTh ?? '',
    showRuleCap: Boolean(seed.showRuleCap),
    // 🆕 per-question quick-reply generation — default OFF (opt-in).
    quickReplyEnabled: Boolean(seed.quickReplyEnabled),
    quickReplyPrompt: seed.quickReplyPrompt ?? '',
    answerType,
    controlType: answerType ? controlTypeForAnswer(answerType) : null,
    answerOptions: [],
    pndCodes: [],
    // 🆕 group question — bound fields (fieldKey kept verbatim, not uppercased).
    boundFields: [],
    // 🆕 income classifier — category catalog (empty until authored).
    incomeCategories: [],
    // 🆕 per-question knowledge refs (vectordbnames).
    knowledgeIds: [],
    // existing fields
    label: seed.label || '',
    helpText: null,
    inputType: isLayout ? 'section_header' : (seed.inputType || inputTypeForAnswer(answerType)),
    options: null,
    visibleWhen: 'Always',
    isRequired: false,
    groupCode: seed.groupCode ?? null,
    sortOrder: seed.sortOrder ?? 0,
    isActive: true,
  };
}

/**
 * Load a `/full` payload into the builder cache: version, flattened ordered
 * items (UI-mapped), per-step metadata (system prompt + knowledge), warnings.
 * @param {TaxQuestionFullDto} fullDto
 * @returns {UiTaxQuestionItem[]}
 */
function loadFull(fullDto) {
  state.currentVersion = (fullDto && fullDto.version) || null;
  state.warnings = (fullDto && Array.isArray(fullDto.warnings)) ? fullDto.warnings.slice() : [];

  // 🆕 step metadata (conversation phases), keyed by UPPER stepCode == groupCode.
  const meta = {};
  const steps = (fullDto && Array.isArray(fullDto.steps)) ? fullDto.steps : [];
  steps.forEach((s) => {
    const code = trimUpper(s.stepCode);
    if (!code) return;
    meta[code] = {
      questionStepID: s.questionStepID ?? null,
      stepCode: code,
      label: s.label || code,
      description: s.description ?? null,
      systemPrompt: s.systemPrompt ?? '',
      answerSystemPrompt: s.answerSystemPrompt ?? '',
      questionSystemPrompt: s.questionSystemPrompt ?? '',
      knowledgeIds: Array.isArray(s.knowledgeIds) ? s.knowledgeIds.slice() : [],
      perPerson: Boolean(s.perPerson),
      sortOrder: s.sortOrder ?? 0,
      isActive: s.isActive !== false,
    };
  });

  const groups = (fullDto && Array.isArray(fullDto.groups)) ? fullDto.groups : [];
  const items = [];
  groups.forEach((group) => {
    const code = trimUpper(group.groupCode);
    // Enrich/fill metadata from the group (covers groups without a backing step row).
    if (code && code !== 'GENERAL') {
      if (!meta[code]) {
        meta[code] = {
          questionStepID: null,
          stepCode: code,
          label: group.groupLabel || groupLabel(code),
          description: null,
          systemPrompt: group.systemPrompt ?? '',
          answerSystemPrompt: group.answerSystemPrompt ?? '',
          questionSystemPrompt: group.questionSystemPrompt ?? '',
          knowledgeIds: Array.isArray(group.knowledgeIds) ? group.knowledgeIds.slice() : [],
          perPerson: Boolean(group.perPerson),
          sortOrder: group.sortOrder ?? 0,
          isActive: true,
        };
      } else {
        if (!meta[code].label) meta[code].label = group.groupLabel || code;
        if (!meta[code].systemPrompt && group.systemPrompt) meta[code].systemPrompt = group.systemPrompt;
        if (!meta[code].answerSystemPrompt && group.answerSystemPrompt) meta[code].answerSystemPrompt = group.answerSystemPrompt;
        if (!meta[code].questionSystemPrompt && group.questionSystemPrompt) meta[code].questionSystemPrompt = group.questionSystemPrompt;
        if ((!meta[code].knowledgeIds || !meta[code].knowledgeIds.length) && Array.isArray(group.knowledgeIds)) {
          meta[code].knowledgeIds = group.knowledgeIds.slice();
        }
        if (!meta[code].perPerson && group.perPerson) meta[code].perPerson = true;
      }
    }
    (group.items || []).forEach((dto) => {
      const ui = mapItemDtoToUi(dto);
      if (!ui.groupCode) ui.groupCode = group.groupCode || null;
      items.push(ui);
    });
  });
  state.groupMeta = meta;
  items.sort((a, b) => (a.sortOrder - b.sortOrder) || ((a.questionItemID || 0) - (b.questionItemID || 0)));
  state.items = items;
  return items;
}

function clearBuilder() {
  state.currentVersion = null;
  state.items = [];
  state.groupMeta = {};
  state.warnings = [];
}

export const taxQuestionStore = {
  state,
  groupLabel,
  controlTypeForAnswer,
  inputTypeForAnswer,
  setConfigVersion,
  setVersions,
  mapItemDtoToUi,
  buildItemRequest,
  buildSteps,
  ensureGroupMeta,
  ensureGroupMetaForCodes,
  groupMetaFor,
  setGroupSystemPrompt,
  setGroupKnowledge,
  updateGroupMeta,
  generateStepCode,
  createSection,
  removeSection,
  createBlankItem,
  loadFull,
  clearBuilder,
};

export default taxQuestionStore;
